/*
If any changes are made to CarouselCardMedium adding/removing wrappers or reordering any children,
  the src/supportAssistant/components/SelectPurchase/LesaNlsPropertyCarouselCard.ui.tsx
  selectors must be updated to match.
*/
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { isString } from 'lib/string/stringUtils'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import clsx from 'clsx'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useHasMounted from 'hooks/useHasMounted'
import getResizableImage from '../getResizableImage'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'

export const CAROUSEL_CARD_WIDTH = 512

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: auto 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
  min-height: ${rem(400)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(520)};
  }

  ${mediaQueryUp.desktop} {
    min-height: ${rem(520)};
  }

  &.variant-opaque {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;

  > picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  &.hover {
    > :nth-child(2) {
      position: absolute;
      inset: 0;
      transition: opacity 0.3s;
      opacity: 0;
    }

    > :nth-child(1) {
      transition: opacity 0.3s;
      opacity: 1;
    }

    ${mediaHoverable} {
      &:hover {
        > :nth-child(1) {
          opacity: 0;
        }

        > :nth-child(2) {
          opacity: 1;
        }
      }
    }
  }
`

const IMAGE_ASPECT_RATIO = '16:9'
const IMAGE_WIDTH = 512
const IMAGE_HEIGHT = 288

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(8)} ${rem(12)};
  pointer-events: none;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

const DetailsContainer = styled(Group)`
  &.variant-opaque {
    padding-block: 0 ${rem(16)};
    padding-inline: ${rem(16)};
  }
`

const UrgencyLabelsWrapper = styled.div`
  span {
    height: auto;
    max-width: 100%;
    white-space: normal;
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  /**
   * @default default
   */
  variant?: 'default' | 'opaque'
  image: App.Image
  images?: Array<App.Image>
  location: string | React.ReactElement
  title: string
  providerName?: string
  to: string
  className?: string
  priceDetails?: React.ReactElement
  bookmarkButton?: React.ReactElement
  urgencyLabels?: React.ReactElement
  imageCornerLabel?: React.ReactElement
  description?: string | React.ReactElement
  rating?: App.OfferRating
  onClick?: () => void
  timeRemainingLabel?: App.OfferUrgencyLabel
  partnershipLabel?: React.ReactElement
  loyaltyPointsElement?: React.ReactElement
  loyaltyPackageUpgradeElement?: React.ReactElement
  /**
   * Inclusions shown on homepage tiles for flash hotel offers.
   */
  taglineInclusions?: React.ReactElement
  productLabel?: React.ReactElement
  /**
   * @default large
   */
  titleVariant?: 'medium' | 'large'
  /**
   * Whether or not the offer tile should load it's image without
   * waiting for the tile to be in view.
   */
  eagerLoadImage?: boolean
}

function CarouselCardMedium(props: Props) {
  const {
    variant = 'default',
    bookmarkButton,
    image: baseImage,
    images,
    location,
    providerName,
    title: heading,
    priceDetails: offerPriceDetails,
    to: offerUrl,
    rating,
    urgencyLabels,
    className,
    description,
    timeRemainingLabel,
    partnershipLabel,
    imageCornerLabel,
    loyaltyPointsElement,
    loyaltyPackageUpgradeElement,
    taglineInclusions,
    productLabel,
    titleVariant = 'large',
    eagerLoadImage,
    ...rest
  } = props

  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()

  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const hoverImage = images?.[1]
  const isDesktop = useScreenSizeUp('tablet')
  const isMounted = useHasMounted()
  const isHoverEnabled = isMounted && isDesktop && !!hoverImage

  const image = useMemo(() => getResizableImage(baseImage ?? images?.[0]), [baseImage, images])
  const resizedHoverImage = useMemo(() => getResizableImage(hoverImage), [hoverImage])

  return (
    <CarouselCardContainer
      {...rest}
      target={isOpenNewTabOfferClickEnabled ? '_blank' : undefined}
      to={offerUrl}
      className={clsx(className, `variant-${variant}`)}
    >
      <ImageContainer
        paletteType="default"
        className={clsx({ hover: isHoverEnabled })}
      >
        <Image
          aspectRatio={IMAGE_ASPECT_RATIO}
          image={image}
          fit="center"
          dpr={2}
          height={IMAGE_HEIGHT}
          width={IMAGE_WIDTH}
          onLoad={handleImageLoaded}
          loading={eagerLoadImage ? 'eager' : 'lazy'}
          fetchPriority={eagerLoadImage ? 'high' : undefined}
        />
        {resizedHoverImage && isHoverEnabled && (
          <Image
            aspectRatio={IMAGE_ASPECT_RATIO}
            image={resizedHoverImage}
            fit="center"
            dpr={2}
            height={IMAGE_HEIGHT}
            width={IMAGE_WIDTH}
            loading="lazy"
          />
        )}
        <ImageOverlay>
          <LabelGroup className="left">
            {timeRemainingLabel?.end && <OfferUrgencyCountdownLabel
              endDate={timeRemainingLabel.end}
              tagPlace="carousel-card-medium"
            />}
            {!timeRemainingLabel && imageCornerLabel}
            {productLabel}
            {partnershipLabel}
          </LabelGroup>
          <TripGuard>
            <div className="right">
              {bookmarkButton}
            </div>
          </TripGuard>
        </ImageOverlay>
      </ImageContainer>
      <DetailsContainer
        direction="vertical"
        verticalAlign="space-between"
        gap={12}
        className={`variant-${variant}`}
      >
        <VerticalSpacer gap={8}>
          <Group direction="horizontal" gap={8} horizontalAlign="space-between">
            <Group direction="vertical">
              {isString(location) ? <BodyText colour="normal-contrast" variant="medium">{location}</BodyText> : location}
              {providerName && <BodyText colour="normal-contrast" variant="medium" weight="bold">{providerName}</BodyText>}
            </Group>
            {rating && <OfferRating
              variant="xs"
              rating={rating}
              onlyNumber="auto"
              inlineLabel
              hideLink
            />}
          </Group>
          {!!heading && <BodyText
            colour="normal-contrast"
            variant={titleVariant}
            weight="bold"
            lineClamp={3}
            desktopLineClamp={2}
          >
            {heading}
          </BodyText>}
          {taglineInclusions}
          {description && isString(description) && <BodyText colour="light-foreground-alt" variant="medium">{description}</BodyText>}
          {description && !isString(description) && description}
          {loyaltyPackageUpgradeElement}
          {loyaltyPointsElement}
          <UrgencyLabelsWrapper>{urgencyLabels}</UrgencyLabelsWrapper>
        </VerticalSpacer>
        <div>
          {offerPriceDetails}
        </div>
      </DetailsContainer>
    </CarouselCardContainer>
  )
}

export default CarouselCardMedium
